import fabric from '@/lib/fabric';
import type { MarqueeShape } from '@canvas/shared-types';
import { isCropOverlay } from '@/lib/cropTool';
import { isObjectLocked } from '@/lib/layerOps';

export const MARQUEE_KEY = 'craftaraMarquee';

export function createMarqueeOverlay(
  bounds: {
    left: number;
    top: number;
    width: number;
    height: number;
  },
  shape: MarqueeShape = 'rect'
): fabric.Object {
  const common = {
    fill: 'rgba(13, 115, 119, 0.12)',
    stroke: '#0d7377',
    strokeWidth: 1,
    strokeDashArray: [4, 3],
    selectable: false,
    evented: false,
    excludeFromExport: true,
    objectCaching: false,
  };

  let overlay: fabric.Object;
  if (shape === 'ellipse') {
    overlay = new fabric.Ellipse({
      left: bounds.left,
      top: bounds.top,
      rx: Math.max(0.5, bounds.width / 2),
      ry: Math.max(0.5, bounds.height / 2),
      originX: 'left',
      originY: 'top',
      ...common,
    });
  } else {
    overlay = new fabric.Rect({
      left: bounds.left,
      top: bounds.top,
      width: Math.max(1, bounds.width),
      height: Math.max(1, bounds.height),
      ...common,
    });
  }
  (overlay as unknown as Record<string, unknown>)[MARQUEE_KEY] = true;
  return overlay;
}

export function isMarqueeOverlay(obj: fabric.Object | null | undefined): boolean {
  return Boolean(obj && (obj as unknown as Record<string, unknown>)[MARQUEE_KEY]);
}

function intersectsRect(
  a: { left: number; top: number; width: number; height: number },
  b: { left: number; top: number; width: number; height: number }
): boolean {
  return (
    a.left < b.left + b.width &&
    a.left + a.width > b.left &&
    a.top < b.top + b.height &&
    a.top + a.height > b.top
  );
}

function pointInEllipse(
  px: number,
  py: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number
): boolean {
  if (rx <= 0 || ry <= 0) return false;
  const dx = (px - cx) / rx;
  const dy = (py - cy) / ry;
  return dx * dx + dy * dy <= 1;
}

function intersectsEllipse(
  b: { left: number; top: number; width: number; height: number },
  ellipse: { left: number; top: number; width: number; height: number }
): boolean {
  const cx = ellipse.left + ellipse.width / 2;
  const cy = ellipse.top + ellipse.height / 2;
  const rx = ellipse.width / 2;
  const ry = ellipse.height / 2;

  const samples = [
    { x: b.left + b.width / 2, y: b.top + b.height / 2 },
    { x: b.left, y: b.top },
    { x: b.left + b.width, y: b.top },
    { x: b.left, y: b.top + b.height },
    { x: b.left + b.width, y: b.top + b.height },
  ];
  if (samples.some((p) => pointInEllipse(p.x, p.y, cx, cy, rx, ry))) return true;

  if (
    cx >= b.left &&
    cx <= b.left + b.width &&
    cy >= b.top &&
    cy <= b.top + b.height
  ) {
    return true;
  }

  return intersectsRect(b, ellipse);
}

/**
 * Select all objects whose bounds intersect the marquee.
 * When `additive`, merge with the current selection (Shift+marquee).
 */
export function selectObjectsInMarquee(
  canvas: fabric.Canvas,
  rect: { left: number; top: number; width: number; height: number },
  options?: { additive?: boolean; shape?: MarqueeShape }
): boolean {
  const shape = options?.shape || 'rect';
  const hits = canvas.getObjects().filter((obj) => {
    if (!obj.visible || isCropOverlay(obj) || isMarqueeOverlay(obj)) return false;
    if (obj.excludeFromExport || isObjectLocked(obj)) return false;
    const b = obj.getBoundingRect(true);
    return shape === 'ellipse' ? intersectsEllipse(b, rect) : intersectsRect(rect, b);
  });

  const additive = Boolean(options?.additive);
  const prev = additive ? canvas.getActiveObjects().slice() : [];
  const merged = new Map<fabric.Object, fabric.Object>();
  prev.forEach((obj) => merged.set(obj, obj));
  hits.forEach((obj) => merged.set(obj, obj));
  const selected = Array.from(merged.values());

  canvas.discardActiveObject();

  if (selected.length === 0) {
    canvas.requestRenderAll();
    return false;
  }

  if (selected.length === 1) {
    canvas.setActiveObject(selected[0]);
  } else {
    const selection = new fabric.ActiveSelection(selected, { canvas });
    canvas.setActiveObject(selection);
  }
  canvas.requestRenderAll();
  return true;
}

/** @deprecated use selectObjectsInMarquee */
export function selectObjectsInRect(
  canvas: fabric.Canvas,
  rect: { left: number; top: number; width: number; height: number },
  options?: { additive?: boolean }
): boolean {
  return selectObjectsInMarquee(canvas, rect, { ...options, shape: 'rect' });
}
